import { useState, useEffect, useCallback, useRef } from "react";
import {
  CAROUSEL_INTERVAL_MS,
  CAROUSEL_PAUSE_RESET_MS,
  CAROUSEL_CLONE_FACTOR,
} from "../config/constants";

/**
 * Generic infinite-scroll carousel hook.
 *
 * @param {number} total - Total number of real items.
 * @param {number} perView - How many items are visible at once.
 * @returns {{
 *   cur: number,
 *   trackOffset: number,
 *   cloneOffset: number,
 *   paused: boolean,
 *   getRealIndex: (n: number) => number,
 *   handleNext: () => void,
 *   handlePrev: () => void,
 *   handleGoTo: (index: number) => void,
 *   handlePause: () => void,
 *   buildClones: (items: any[]) => any[],
 * }}
 */
export function useCarousel(total, perView) {
  const [cur, setCur]       = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef            = useRef(null);

  /** Number of real items prepended before the real list in the cloned track. */
  const cloneOffset = total;

  const getRealIndex = useCallback(
    (n) => ((n % total) + total) % total,
    [total]
  );

  const getTrackOffset = useCallback(
    (realIdx) => -((cloneOffset + realIdx) * (100 / perView)),
    [cloneOffset, perView]
  );

  const [trackOffset, setTrackOffset] = useState(() => getTrackOffset(0));

  useEffect(() => {
    setTrackOffset(getTrackOffset(cur));
  }, [cur, getTrackOffset]);

  const handleNext = useCallback(() => {
    setPaused(true);
    setCur((c) => getRealIndex(c + 1));
  }, [getRealIndex]);

  const handlePrev = useCallback(() => {
    setPaused(true);
    setCur((c) => getRealIndex(c - 1));
  }, [getRealIndex]);

  const handleGoTo = useCallback(
    (index) => {
      setPaused(true);
      setCur(getRealIndex(index));
    },
    [getRealIndex]
  );

  const handlePause = useCallback(() => setPaused(true), []);

  // Resume autoplay after pause
  useEffect(() => {
    if (!paused) return;
    const id = setTimeout(() => setPaused(false), CAROUSEL_PAUSE_RESET_MS);
    return () => clearTimeout(id);
  }, [paused, cur]);

  // Autoplay — capture interval ID locally so cleanup always clears the right one
  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setCur((c) => getRealIndex(c + 1)),
      CAROUSEL_INTERVAL_MS
    );
    timerRef.current = id;
    return () => clearInterval(id);
  }, [paused, getRealIndex]);

  /**
   * Returns CAROUSEL_CLONE_FACTOR copies of `items` flattened into one array.
   * Stable reference — CAROUSEL_CLONE_FACTOR is a module constant, not state.
   */
  const buildClones = useCallback(
    (items) => Array.from({ length: CAROUSEL_CLONE_FACTOR }, () => items).flat(),
    []
  );

  return {
    cur,
    trackOffset,
    paused,
    cloneOffset,
    getRealIndex,
    handleNext,
    handlePrev,
    handleGoTo,
    handlePause,
    buildClones,
  };
}
